<template>
  <div class="space-y-4">
    <!-- Selected Features -->
    <div v-if="selectedFeatures.length > 0" class="space-y-2">
      <label class="text-sm font-medium text-slate-700">
        Wybrane cechy produktu ({{ selectedFeatures.length }})
      </label>
      <div class="flex flex-wrap gap-2">
        <div 
          v-for="feature in selectedFeatures" 
          :key="feature.id"
          class="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-lg text-sm"
        >
          <span class="font-medium text-blue-800">{{ feature.feature_name }}:</span>
          <span class="text-blue-700">{{ getValueFromLabel(feature.label) }}</span>
          <button 
            type="button"
            class="ml-1 text-blue-600 hover:text-blue-800 transition-colors"
            @click="removeFeature(feature.id)"
            :aria-label="`Usuń cechę ${feature.label}`"
          >
            <font-awesome-icon icon="times" class="w-3 h-3" />
          </button>
        </div>
      </div>
    </div>

    <!-- Add Feature Button -->
    <div>
      <button 
        type="button"
        class="inline-flex items-center gap-2 px-4 py-2 border border-slate-300 rounded-md text-sm font-medium text-slate-700 bg-white hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
        @click="openPicker"
      >
        <font-awesome-icon icon="plus" class="w-4 h-4" />
        {{ selectedFeatures.length > 0 ? 'Dodaj kolejną cechę' : 'Dodaj cechę produktu' }}
      </button>
    </div>

    <!-- Help Text -->
    <p class="text-xs text-slate-500">
      Cechy produktu pomagają kupującym lepiej zrozumieć Twoją ofertę. Możesz dodać cechy takie jak marka, stan, kolor itp.
    </p>

    <!-- Feature Picker Modal -->
    <FeaturePickerModal 
      :show="showPicker" 
      @close="showPicker = false" 
      @select="handleFeatureSelect" 
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue';
import FeaturePickerModal from './FeaturePickerModal.vue';
import { featureApi, type SelectedFeatureValue } from '../services/features.api';
import { useApiConfig } from '../../../composables/useApi';

interface Props {
  modelValue?: number[];
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => [],
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: number[]): void;
}>();

const showPicker = ref(false);
const selectedFeatures = ref<SelectedFeatureValue[]>([]);
const api = useApiConfig();

function openPicker() {
  showPicker.value = true;
}

function handleFeatureSelect(feature: SelectedFeatureValue) {
  // Ensure only one value per feature group (replace if same feature)
  const existingIndex = selectedFeatures.value.findIndex(f => f.feature_id === feature.feature_id);
  if (existingIndex >= 0) {
    selectedFeatures.value[existingIndex] = feature;
  } else {
    selectedFeatures.value.push(feature);
  }
  
  // Emit the updated array of IDs
  const ids = selectedFeatures.value.map(f => f.id);
  emit('update:modelValue', ids);
  
  showPicker.value = false;
}

function removeFeature(featureId: number) {
  const index = selectedFeatures.value.findIndex(f => f.id === featureId);
  if (index >= 0) {
    selectedFeatures.value.splice(index, 1);
    
    // Emit the updated array of IDs
    const ids = selectedFeatures.value.map(f => f.id);
    emit('update:modelValue', ids);
  }
}

function getValueFromLabel(label: string): string {
  // Extract value part after colon, or return the whole label if no colon
  const parts = label.split(': ');
  return parts.length > 1 ? parts.slice(1).join(': ') : label;
}

async function loadSelectedFeatures(featureIds: number[]) {
  if (featureIds.length === 0) {
    selectedFeatures.value = [];
    return;
  }

  try {
    const features = await featureApi.getFeatureValuesByIdsWithConfig(api, featureIds);
    selectedFeatures.value = features;
  } catch (e) {
    console.error('Failed to load selected features', e);
    selectedFeatures.value = [];
  }
}

// Watch for changes in modelValue (when parent component updates the selection)
watch(() => props.modelValue, (newIds) => {
  if (!newIds || newIds.length === 0) {
    selectedFeatures.value = [];
    return;
  }

  // Check if we need to reload features (IDs changed)
  const currentIds = selectedFeatures.value.map(f => f.id).sort();
  const newIdsSorted = [...newIds].sort();
  
  if (JSON.stringify(currentIds) !== JSON.stringify(newIdsSorted)) {
    loadSelectedFeatures(newIds);
  }
}, { immediate: true });

// Load initial features on mount
onMounted(() => {
  if (props.modelValue && props.modelValue.length > 0) {
    loadSelectedFeatures(props.modelValue);
  }
});
</script>
